import * as React from 'react';
import { DefaultOptionType } from 'antd/lib/cascader';
import { HelperPopover, NiCascader, NiSelect } from '@component/index';
import { API, blueColor } from '@config/index';
import {
  FrameType,
  FrameItem,
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  GenericType,
  ExternalType,
  Attribute,
  ParameterType,
  ReferenceNode,
  ServiceInfo
} from '@interface/index';
import { localizedString } from '@locale/index';
import { availableStateFilter, getFormattedName, getPackageName, packageName } from '@lib/index';
import { flatten } from 'lodash';

export const CompGroup = ({
  title,
  helpString,
  comp
}: {
  title: string;
  helpString?: string;
  comp: JSX.Element;
}) => {
  return (
    <div
      className="sug-box"
      style={{
        display: 'flex',
        margin: '0 0 16px 0'
      }}
    >
      <span
        style={{
          display: 'flex',
          flex: '0 90px',
          justifyContent: 'flex-end',
          alignItems: 'center',
          margin: '4px 64px 0 0',
          fontSize: 14,
          color: 'rgba(0, 0, 0, 0.87)',
          textAlign: 'right',
          fontWeight: 400
        }}
      >
        {title}
        {!!helpString ? (<div
          className="pop-container"
          style={{
            width: 0
          }}
        >
          <HelperPopover text={helpString} />
        </div>) : (
          ''
        )}
      </span>
      <div style={{ flex: '1' }}>{comp}</div>
    </div>
  );
};

const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const highlightText = (text: string, highlight: string): React.ReactNode => {
  if (!highlight || highlight.trim() === '') {
    return text;
  }

  const lowerCaseText = text.toLowerCase();
  const lowerCaseHighlight = highlight.toLowerCase();

  if (lowerCaseText.includes(lowerCaseHighlight)) {
    const startIndex = lowerCaseText.indexOf(lowerCaseHighlight);
    const endIndex = startIndex + lowerCaseHighlight.length;

    return (
      <>
        {text.substring(0, startIndex)}
        <span style={{ color: blueColor, fontWeight: 'bold' }}>{text.substring(startIndex, endIndex)}</span>
        {text.substring(endIndex)}
      </>
    );
  }

  if (lowerCaseHighlight.split('').every(char => lowerCaseText.includes(char))) {

    const escapedChars = highlight.split('').map(char => escapeRegExp(char));
    const regex = new RegExp(`(${escapedChars.join('|')})`, 'gi');

    return text.split(regex).map((part, i) =>
      escapedChars.some(escapedChar => new RegExp(`^${escapedChar}$`, 'i').test(part))
        ? <span key={i} style={{ fontWeight: 'bold' }}>{part}</span>
        : part
    );
  }

  return text;
};

export const TypeSelect = ({
  params,
  frame,
  data,
  onChange,
  disabled,
  style,
  placeholder
}: {
  params?: {
    availableIntents?: IntentInfo[];
    availableComposites?: CompositeInfo[];
    availableEntities?: EntityInfo[];
    availableServices?: ServiceInfo[];
    genericTypes?: GenericType[];
    externalTypes?: ExternalType[];
    availableDialogActs?: IntentInfo[];
  };
  frame?: FrameItem;
  data?: ReferenceNode;
  onChange: (next: ReferenceNode) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
}) => {

  const [searchText, setSearchText] = React.useState<string>('');
  const [filteredOptions, setFilteredOptions] = React.useState<DefaultOptionType[]>([]);

  const [options, setOptions] = React.useState<DefaultOptionType[]>([]);

  React.useEffect(() => {

    if (!params) {
      setOptions([]);
      setFilteredOptions([]);
      return;
    }
    const { availableIntents = [], availableComposites = [], availableDialogActs = [], availableEntities = [], availableServices = [], externalTypes = [], genericTypes = [] } = params;

    let res: any = [];

    for (const intent of availableIntents) {
      res.push({
        value: intent.id,
        label: getFormattedName(intent),
        type: FrameType.intent,
        isLeaf: true,
        packageName: getPackageName(intent)
      });
    }

    for (const da of availableDialogActs) {
      res.push({
        value: da.id,
        label: getFormattedName(da),
        type: FrameType.intent,
        isLeaf: true,
        packageName: getPackageName(da)
      });
    }

    for (const composite of availableComposites) {

      let optDisabled = false;
      if (frame && frame.type === FrameType.service &&
        composite.storageEnable &&
        !!composite.attributes.find((attr: Attribute) =>
          attr.proto && attr.proto.type === ParameterType.COMPOSITE)) {
        optDisabled = true;
      }

      res.push({
        value: composite.id,
        label: getFormattedName(composite),
        type: FrameType.composite,
        disabled: optDisabled,
        isLeaf: true,
        packageName: getPackageName(composite)
      });
    }

    for (const entity of availableEntities) {
      res.push({
        value: entity.id,
        label: getFormattedName(entity),
        type: FrameType.entity,
        isLeaf: true,
        packageName: getPackageName(entity)
      });
    }

    for (const service of availableServices) {
      res.push({
        value: service.id,
        label: getFormattedName(service),
        type: FrameType.service,
        isLeaf: true,
        packageName: getPackageName(service)
      });
    }

    for (const tp of externalTypes) {
      res.push({
        value: tp.id,
        label: tp.name,
        type: FrameType.external,
        isLeaf: true
      });
    }

    for (const tp of genericTypes) {
      res.push({
        value: tp.id,
        label: tp.name,
        type: FrameType.generic,
        isLeaf: true
      });
    }

    res = res.sort((a: any, b: any) => a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1);
    setOptions(res);
    setFilteredOptions(res);

  }, [params]);

  const handleSearch = (value: string) => {
    setSearchText(value);

    if (!value) {
      setFilteredOptions(options);
      return;
    }

    const filtered = options.filter((option: any) => {
      const lowerCaseValue = value.toLowerCase();
      const lowerCaseLabel = (option.label as string).toLowerCase();

      // 优先级 1: 以 search text 开头
      if (lowerCaseLabel.startsWith(lowerCaseValue)) {
        option.priority = 1;
        return true;
      }

      if (option.packageName && option.packageName.toLowerCase().startsWith(lowerCaseValue)) {
        option.priority = 2;
      }

      // 优先级 2: 包含 search text
      if (lowerCaseLabel.includes(lowerCaseValue)) {
        option.priority = 3;
        return true;
      }

      // 优先级 3: 穿插包含某些字母 (fuzzy match)
      if (lowerCaseValue.split('').every(char => lowerCaseLabel.includes(char))) {
        option.priority = 4;
        return true;
      }

      return false;

    }).sort((a: DefaultOptionType, b: DefaultOptionType) => {

      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }

      return (a.label as string).toLowerCase() < (b.label as string).toLowerCase() ? -1 : 1;
    }); // 按优先级排序

    setFilteredOptions(filtered);
  };

  function onChangeFn(value: string) {
    const selected = options.find(option => option.value === value);
    onChange({
      id: (selected && selected.value) as string,
      type: selected && selected.type,
      name: (selected && selected.label) as string
    });
  }

  const handleDropdownVisibleChange = React.useCallback((open: boolean) => {
    if (!open) {
      setSearchText('');
    }
  }, []);

  return (
    <NiSelect
      showSearch
      placement="bottomLeft"
      listHeight={300}
      // filterOption={(input, option: any) =>
      //   (option.label || '').toLowerCase().includes(input.toLowerCase())
      // }
      onDropdownVisibleChange={handleDropdownVisibleChange}
      filterOption={false}
      onSearch={handleSearch}
      disabled={disabled}
      style={{
        minWidth: 200,
        width: 300,
        ...style
      }}
      allowClear={false}
      value={
        data
          ? data.id
          : null
      }
      // options={options}
      onChange={onChangeFn}
      placeholder={placeholder || localizedString('data_query_frame_placeholder')}
    >
      {(searchText.length ? filteredOptions : options).map(option => (
        <NiSelect.Option key={option.value} value={option.value}>
          {highlightText(option.label as string, searchText)}
        </NiSelect.Option>
      ))}
    </NiSelect>
  );
};

export interface TypeDefinition {
  baseType: ReferenceNode;
  multiValued?: boolean;
  nullable?: boolean;
}

export const TypeDefinitionSelect = ({
  params,
  forSlot,
  frame,
  data,
  onChange,
  disabled,
  style,
  placeholder
}: {
  params?: {
    availableIntents?: IntentInfo[];
    availableComposites?: CompositeInfo[];
    availableEntities?: EntityInfo[];
    availableServices?: ServiceInfo[];
    genericTypes?: GenericType[];
    externalTypes?: ExternalType[];
    availableDialogActs?: IntentInfo[];
  };
  forSlot?: boolean;
  frame?: FrameItem;
  data?: TypeDefinition;
  onChange: (next: TypeDefinition) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  placeholder?: string;
}) => {

  const [searchText, setSearchText] = React.useState<string>('');
  const [filteredOptions, setFilteredOptions] = React.useState<DefaultOptionType[]>([]);

  const [baseTypeOptions, setBaseTypeOptions] = React.useState<DefaultOptionType[]>([]);
  const [options, setOptions] = React.useState<DefaultOptionType[]>([]);

  React.useEffect(() => {

    if (!params) {
      setOptions([]);
      setFilteredOptions([]);
      return;
    }
    const { availableIntents = [], availableComposites = [], availableDialogActs = [], availableEntities = [], availableServices = [], externalTypes = [], genericTypes = [] } = params;

    let res: any = [];

    for (const intent of availableIntents) {
      res.push({
        value: intent.id,
        label: getFormattedName(intent),
        type: FrameType.intent,
        isLeaf: true,
        packageName: getPackageName(intent)
      });
    }

    for (const da of availableDialogActs) {
      res.push({
        value: da.id,
        label: getFormattedName(da),
        type: FrameType.intent,
        isLeaf: true,
        packageName: getPackageName(da)
      });
    }

    for (const composite of availableComposites) {

      let optDisabled = false;
      if (frame && frame.type === FrameType.service &&
        composite.storageEnable &&
        !!composite.attributes.find((attr: Attribute) =>
          attr.proto && attr.proto.type === ParameterType.COMPOSITE)) {
        optDisabled = true;
      }

      res.push({
        value: composite.id,
        label: getFormattedName(composite),
        type: FrameType.composite,
        disabled: optDisabled,
        isLeaf: true,
        packageName: getPackageName(composite)
      });
    }

    for (const entity of availableEntities) {
      res.push({
        value: entity.id,
        label: getFormattedName(entity),
        type: FrameType.entity,
        isLeaf: true,
        packageName: getPackageName(entity)
      });
    }

    for (const service of availableServices) {
      res.push({
        value: service.id,
        label: getFormattedName(service),
        type: FrameType.service,
        isLeaf: true,
        packageName: getPackageName(service)
      });
    }

    for (const tp of externalTypes) {
      res.push({
        value: tp.id,
        label: tp.name,
        type: FrameType.external,
        isLeaf: true
      });
    }

    for (const tp of genericTypes) {
      res.push({
        value: tp.id,
        label: tp.name,
        type: FrameType.generic,
        isLeaf: true
      });
    }

    res = res.sort((a: any, b: any) => a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1);
    setBaseTypeOptions(res);
    const fullOptions: DefaultOptionType[] = flatten(res.map((option: any) => {
      const subopts: DefaultOptionType[] = []

      if (!forSlot) {
        subopts.push({
          ...option,
          value: `${option.value}-false-false`,
          label: option.label
        });
        subopts.push({
          ...option,
          value: `${option.value}-true-false`,
          label: option.label + '[]'
        });
      }
      subopts.push({
        ...option,
        value: `${option.value}-false-true`,
        label: option.label + '?'
      });
      subopts.push({
        ...option,
        value: `${option.value}-true-true`,
        label: option.label + '[]?'
      });

      return subopts;
    }));
    setOptions(fullOptions);
    setFilteredOptions(fullOptions);

  }, [params]);

  const handleSearch = (value: string) => {
    setSearchText(value);

    if (!value) {
      setFilteredOptions(options);
      return;
    }

    const filtered = options.filter((option: any) => {
      const lowerCaseValue = value.toLowerCase();
      const lowerCaseLabel = (option.label as string).toLowerCase();

      // 优先级 1: 以 search text 开头
      if (lowerCaseLabel.startsWith(lowerCaseValue)) {
        option.priority = 1;
        return true;
      }

      if (option.packageName && option.packageName.toLowerCase().startsWith(lowerCaseValue)) {
        option.priority = 2;
      }

      // 优先级 2: 包含 search text
      if (lowerCaseLabel.includes(lowerCaseValue)) {
        option.priority = 3;
        return true;
      }

      // 优先级 3: 穿插包含某些字母 (fuzzy match)
      if (lowerCaseValue.split('').every(char => lowerCaseLabel.includes(char))) {
        option.priority = 4;
        return true;
      }

      return false;

    }).sort((a: DefaultOptionType, b: DefaultOptionType) => {

      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }

      return (a.label as string).toLowerCase() < (b.label as string).toLowerCase() ? -1 : 1;
    }); // 按优先级排序

    setFilteredOptions(filtered);
  };

  function onChangeFn(value: string) {

    const splits = value.split('-');
    const baseType = baseTypeOptions.find(option => option.value === splits[0]);
    const multiValued = splits[1] === 'true';
    const nullable = splits[2] === 'true';

    onChange({
      multiValued,
      nullable,
      baseType: {
        id: (baseType && baseType.value) as string,
        type: baseType && baseType.type,
        name: (baseType && baseType.label) as string
      }
    });
  }

  const handleDropdownVisibleChange = React.useCallback((open: boolean) => {
    if (!open) {
      setSearchText('');
    }
  }, []);

  return (
    <NiSelect
      showSearch
      placement="bottomLeft"
      listHeight={300}
      // filterOption={(input, option: any) =>
      //   (option.label || '').toLowerCase().includes(input.toLowerCase())
      // }
      onDropdownVisibleChange={handleDropdownVisibleChange}
      filterOption={false}
      onSearch={handleSearch}
      disabled={disabled}
      style={{
        minWidth: 200,
        width: 300,
        ...style
      }}
      allowClear={false}
      value={
        data
          ? `${data.baseType.id || ''}-${data.multiValued}-${data.nullable}`
          : null
      }
      // options={options}
      onChange={onChangeFn}
      placeholder={placeholder || localizedString('data_query_frame_placeholder')}
    >
      {(searchText.length ? filteredOptions : options).map(option => (
        <NiSelect.Option key={option.value} value={option.value}>
          {highlightText(option.label as string, searchText)}
        </NiSelect.Option>
      ))}
    </NiSelect>
  );
};
